import axios, {AxiosError, isAxiosError} from 'axios';

import {apiUrlV2} from '../../../utils/constants';

const api = axios.create({
  baseURL: apiUrlV2,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface CheckoutResponse {
  sessionId: string;
  requestId: string;
  currency: string;
  coinSerial: string;
  amount: number;
  successCallbackUrl: string;
  errorCallbackUrl: string;
  orderDetails: OrderDetail[];
  deliveryDetails: DeliveryDetail;
  paymentMethods: string[];
  createdAt: string;
  message: string;
}

export interface OrderDetail {
  name: string;
  currency: string;
  quantity: number;
  description: string;
  price: number;
}

export interface DeliveryDetail {
  currency: string;
  fee: number;
}

export const checkout = async (id: string): Promise<CheckoutResponse> => {
  try {
    const response = await api.get<CheckoutResponse>(
      `/v1/hosted-checkout/${id}`
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error('error occurred while fetching checkout data');
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface CardPaymentRequest {
  coinSerial: string;
  amount: number;

  cardInfo?: CardInfo; // optional, only present for card payments
  billingInfo?: BillingInfo; // optional, only present for card payments
  notification?: CheckoutNotificationData; // optional, only present if receipt requested
}

export interface CardInfo {
  cardNumber: string;
  cardExpirationMonth: string; // MM
  cardExpirationYear: string; // YY or YYYY
  cardCvv: string;
}

export interface BillingInfo {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  country: string;
  administrativeArea: string;
  locality: string;
  address1: string;
  postalCode: string;
}

export interface MpesaPaymentRequest {
  createTxReq: {
    type: string;
    amount: number;
    method: {
      gateProviderId: string;
      way: string;
    };
    coin: string;
  };
  dataReq: {
    optionName: string;
    fields: {
      phoneNumber: string;
    };
  };
  webhookUrl: string;
  successRedirectUrl: string;
  errorRedirectUrl: string;
  notification?: CheckoutNotificationData; // optional, only present if receipt requested
}

export interface AirtelPaymentRequest {
  createTxReq: {
    type: string;
    amount: number;
    method: {
      gateProviderId: string;
      way: string;
    };
    coin: string;
  };
  dataReq: {
    optionName: string;
    fields: {
      phoneNumber: string;
      countryCode?: string; // Ensure country code is included in the request
    };
  };
  webhookUrl: string;
  successRedirectUrl: string;
  errorRedirectUrl: string;
  notification?: CheckoutNotificationData; // optional, only present if receipt requested
}

export interface TransactionResponse {
  message: string;
  transaction?: {
    id: string;
    orderId: number;
    deviceId: string;
    deviceOrderId: string;
    type: string;
    status: string;
    coin: {
      id: string;
      serial: string;
      name: string;
      amount: number;
      availableAmount: number;
      futureAmount: number;
      heldAmount: number;
      creditLimit: number;
      currency: {
        id: string;
        sn: string;
        code: string;
        symbol: string;
        type: string;
        fraction: number;
        scale: number;
      };
      active: boolean;
      type: string;
      main: boolean;
      accounting: boolean;
      smartCards: unknown[];
    };
    paymentMethod: {
      gateProvider: {
        id: string;
        name: string;
        debtAllowed: boolean;
        active: boolean;
        gate: {
          name: string;
        };
      };
      way: string;
    };
    sourceAmount: number;
    payerData: Record<string, unknown>;
    providerData: Record<string, unknown>;
    processId: string;
  };
  // Optional form (only present for Cybersource / CARD)
  form?: {
    url: string;
    method: 'POST' | 'GET' | string;
    parameters: {
      accessToken?: string; // for Cybersource/Cardinal
      [key: string]: string | undefined; // allows other provider-specific params
    };
  };
}

export const mpesaPayment = async (
  id: string,
  data: MpesaPaymentRequest
): Promise<TransactionResponse> => {
  try {
    const response = await api.post<TransactionResponse>(
      `/v1/hosted-checkout/${id}/mpesa`,
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error('error occurred while fetching checkout data');
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export const airtelPayment = async (
  id: string,
  data: AirtelPaymentRequest
): Promise<TransactionResponse> => {
  try {
    const response = await api.post<TransactionResponse>(
      `/v1/hosted-checkout/${id}/airtel`,
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error('error occurred while fetching checkout data');
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export const cardPayment = async (
  id: string,
  data: CardPaymentRequest
): Promise<TransactionResponse> => {
  try {
    const response = await api.post<TransactionResponse>(
      `/v1/hosted-checkout/${id}/card`,
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error('error occurred while fetching checkout data');
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface Cardinal3DSResponse {
  message: string;
  transaction?: {
    id?: string; // maps to clientReferenceInformation.code
    status?: string; // maps to status
    payerData?: Record<string, unknown>;
    providerData?: Record<string, unknown>;
    // optional: other fields if available
  };
  form?: {
    url: string; // acsUrl
    method: 'POST' | 'GET' | string;
    parameters: {
      accessToken?: string;
      acsTransactionId?: string;
      authenticationTransactionId?: string;
      paresStatus?: string;
      stepUpUrl?: string;
      threeDSServerTransactionId?: string;
      [key: string]: string | undefined; // allows other provider-specific params
    };
  };
  errorInformation?: {
    reason?: string;
    message?: string;
    details?: unknown[];
  };
}

export interface BillTo {
  firstName: string;
  lastName: string;
  email: string;
  address1?: string;
  administrativeArea?: string;
  country?: string;
  locality?: string;
  postalCode?: string;
}

export interface CountryData {
  name: string;
  country: string;
  country_code: string;
  continent_code: string;
  city: string;
}

export interface GeoJSCountryData {
  name?: {
    common: string;
  };
  cca2: string;
}

export interface BinLookupRequest {
  cardNumber: string;
}

export interface BinLookupResponse {
  message: string;
  account_funding_source: string;
  bin: string;
  brand_name: string;
  card_product: string;
  created_at: string;
  deleted_at: string;
  id: string;
  issuer_name: string;
  updated_at: string;
  country_code: string;
  error: string;
}

export interface CardBinData {
  type: string;
  brandName: string;
  maxLength: string;
  credentialType: string;
  brands: string[];
}

export interface CardBinFeatures {
  accountFundingSource: string;
  cardProduct: string;
  cardPlatform: string;
}

export interface CardBinInformation {
  card: CardBinData;
  features: CardBinFeatures;
}

export interface CardBinIssuerInformation {
  name: string;
  country: string;
  binLength: string;
  accountPrefix: string;
}

export interface RawCardBinData {
  id: string;
  submitTimeUtc: string;
  status: string;
  paymentAccountInformation: CardBinInformation;
  issuerInformation: CardBinIssuerInformation;
}

export interface CardBinInfoResponse {
  rawData: string;
  rawDataMediaType: 'application/json';
}

export const binLookup = async (
  id: string,
  data: BinLookupRequest
): Promise<RawCardBinData> => {
  try {
    const response = await api.post<CardBinInfoResponse>(
      `/v1/hosted-checkout/${id}/card/bin`,
      data
    );
    return JSON.parse(response?.data?.rawData) as RawCardBinData;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<BinLookupResponse>;
      if (axiosError.response?.data?.message) {
        throw new Error(axiosError.response.data.message);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface ClientReferenceInformation {
  code: string;
}

export interface ConsumerAuthenticationInformation {
  accessToken: string;
  acsTransactionId: string;
  acsUrl: string;
  authenticationTransactionId: string;
  challengeRequired: 'Y' | 'N';
  pareq: string;
  paresStatus: string;
  specificationVersion: string;
  stepUpUrl: string;
  threeDSServerTransactionId: string;
  veresEnrolled: 'Y' | 'N';
  directoryServerTransactionId: string;
  acsOperatorID: string;
  acsReferenceNumber: string;
}

export interface ErrorInformation {
  reason: string;
  message: string;
  details: unknown[];
}

export interface RawCheckoutData {
  id: string;
  submitTimeUtc: string;
  status: string;
  clientReferenceInformation: ClientReferenceInformation;
  consumerAuthenticationInformation: ConsumerAuthenticationInformation;
  errorInformation: ErrorInformation;
}

export interface CardAuthRequest {
  txId: string;
}

export interface CardAuthResponse {
  message: string;
  rawData: string;
  rawDataMediaType: 'application/json';
}

export const cardPaymentAuth = async (
  id: string,
  data: CardAuthRequest
): Promise<RawCheckoutData> => {
  try {
    const response = await api.post<CardAuthResponse>(
      `/v1/hosted-checkout/${id}/card/authenticate`,
      data
    );
    return JSON.parse(response?.data?.rawData) as RawCheckoutData;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<CardAuthResponse>;
      if (axiosError.response?.data?.message) {
        throw new Error(axiosError.response?.data?.message);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface CheckoutNotificationData {
  channel: string;
  to: string;
}

export interface CheckoutTxStatusRequest {
  txId: string;
  sessionId: string;
  processId: string;
}

export interface CheckoutTxStatusResponse {
  message: string;
  process: {
    status: string;
  };
}

export const checkoutTxPolling = async (
  id: string,
  data: CheckoutTxStatusRequest
): Promise<CheckoutTxStatusResponse> => {
  try {
    const response = await api.post<CheckoutTxStatusResponse>(
      `/v1/hosted-checkout/${id}/status`,
      data
    );
    return response?.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<CheckoutTxStatusResponse>;
      if (axiosError.response?.data?.message) {
        throw new Error(axiosError.response?.data?.message);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface QRRequest {
  description: string;
  phoneNumber?: string;
}

export interface QRResponse {
  message: string;
  paymentIdentifier: string;
  qrCodeMediaFileLink: string;
}

export const fetchQRCode = async (
  id: string,
  data: QRRequest
): Promise<QRResponse> => {
  try {
    const response = await api.post<QRResponse>(
      `/v1/hosted-checkout/${id}/cashiapay`,
      data
    );
    return response?.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<QRResponse>;
      if (axiosError.response?.data?.message) {
        throw new Error(axiosError.response?.data?.message);
      }
    }
    throw new Error('An unexpected error occurred during checkout');
  }
};

export interface QRStatusRequest {
  paymentIdentifier: string;
}

export interface QRStatusResponse {
  message: string;
  status: string;
  statusDescription: string;
}

export const fetchQRCodeStatus = async (
  id: string,
  data: QRStatusRequest
): Promise<QRStatusResponse> => {
  try {
    const response = await api.post<QRStatusResponse>(
      `/v1/hosted-checkout/${id}/cashiapay/status`,
      data
    );
    return response?.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<QRStatusResponse>;
      if (axiosError.response?.data?.message) {
        throw new Error(axiosError.response?.data?.message);
      }
    }
    throw new Error('An unexpected error occurred during qr status check');
  }
};

export type MessageData = {
  MessageType: string;
  SessionId: string;
  status: boolean;
};
